import type { PropsWithChildren, ReactNode } from "react";

import { ConsentText } from "@/constants/Onboarding";
import { TextTestID } from "@/constants/TestID";
import { UserConsentType } from "@/constants/User";
import type { AppUserSliceState } from "@/store/reducers/appUser";
import { CheckBox, Text } from "@/ui/core";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";
import { getPreferredFirstName } from "@/utils/user.util";

interface ConsentCardProps extends PropsWithChildren<ViewWithTestID> {
  children?: ReactNode;
  consentType: UserConsentType;
  isAppUserPatient?: boolean;
  isChecked?: boolean;
  onChange?: (isChecked: boolean) => Promise<void>;
  onPatientConsentChange?: ({
    isChecked,
    patientId,
  }: PatientConsentChangeProps) => Promise<void>;
  patientConsentStates?: Record<string, boolean>;
  patientsNeedingConsent?: AppUserSliceState[] | LinkedPatientProfile[];
  title?: ReactNode;
}

const ConsentCard = ({
  children,
  consentType,
  isAppUserPatient,
  isChecked,
  onChange,
  onPatientConsentChange,
  patientConsentStates: initialPatientConsentStates,
  patientsNeedingConsent: initialPatientsNeedingConsent,
  testID,
  title,
}: ConsentCardProps) => {
  const patientConsentStates = initialPatientConsentStates ?? {};
  const patientsNeedingConsent = initialPatientsNeedingConsent ?? [];
  return (
    <BaseCard testID={joinTestIDs(testID, TextTestID.Card)}>
      <VStack gap={18}>
        {consentType === UserConsentType.TermsAndPrivacy && (
          <HStack alignItems="center" gap={12}>
            <CheckBox
              isChecked={isChecked}
              onChange={onChange}
              testID={joinTestIDs(testID, TextTestID.Checkbox)}
            />
            <Box flex={1}>{title}</Box>
          </HStack>
        )}
        {consentType === UserConsentType.Recording &&
          patientsNeedingConsent.map((patient) => (
            <HStack alignItems="center" gap={12} key={patient.externalId}>
              <CheckBox
                isChecked={patientConsentStates[patient.externalId] || false}
                onChange={(isSelected) =>
                  onPatientConsentChange?.({
                    isChecked: isSelected,
                    patientId: patient.externalId,
                  })
                }
                testID={joinTestIDs(testID, `checkbox-${patient.externalId}`)}
              />
              <Text
                flexShrink={1}
                testID={joinTestIDs(testID, `title-${patient.externalId}`)}
                variant="bodyMedium"
              >
                {
                  // if user is patient, show default consent title, otherwise show the patient's name in the title
                }
                {ConsentText.RecordingConsentTitle(
                  isAppUserPatient
                    ? undefined
                    : getPreferredFirstName({
                        chosenName: patient.chosenName,
                        firstName: patient.firstName,
                      }),
                )}
              </Text>
            </HStack>
          ))}
        {children && <VStack gap={8}>{children}</VStack>}
      </VStack>
    </BaseCard>
  );
};

export type { ConsentCardProps };
export default ConsentCard;
